<template>
  <div
      ref="ball"
      class="float-ball"
      :class="{ 'dragging': dragging }"
      :style="ballStyle"
      @mousedown.stop="startDrag"
      @touchstart.stop="handleTouchStart"
  >
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'FloatBall',
  props: {
    initialPosition: {
      type: Object,
      default: () => ({ x: 90, y: 50 })
    },
    edgeMargin: {
      type: Number,
      default: 10
    },
    size: {
      type: Number,
      default: 60
    }
  },
  data() {
    return {
      position: { x: 0, y: 0 },
      dragging: false,
      windowSize: { width: window.innerWidth, height: window.innerHeight },
      clickTimer: null,
      startBallPosition: { x: 0, y: 0 },  // 新增：记录球初始位置
      startPointerPosition: { x: 0, y: 0 }, // 新增：记录指针初始位置
      dragEndTimeout: null // 新增：用于处理拖拽结束的延迟
    };
  },
  computed: {
    ballStyle() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        borderRadius: '50%',
        // backgroundColor: '#42b983',
        position: 'fixed',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: this.dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        boxShadow: this.dragging
            ? '0 6px 20px rgba(0,0,0,0.3)'
            : '0 2px 10px rgba(0,0,0,0.2)',
        transition: this.dragging ? 'none' : 'left 0.3s ease, top 0.3s ease, box-shadow 0.2s ease',
        touchAction: 'none',
        background: 'transparent',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '20px'
      };
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('mouseup', this.stopDrag);
    window.addEventListener('touchend', this.handleTouchEnd);

    // 初始位置
    this.position = {
      x: (this.windowSize.width * this.initialPosition.x) / 100 - this.size / 2,
      y: (this.windowSize.height * this.initialPosition.y) / 100 - this.size / 2
    };
    this.$nextTick(this.snapToEdge);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('touchend', this.handleTouchEnd);

    // 清除定时器
    if (this.clickTimer) clearTimeout(this.clickTimer);
    if (this.dragEndTimeout) clearTimeout(this.dragEndTimeout);
  },
  methods: {
    startDrag(event) {
      this.dragging = true;
      this.startPointerPosition = {
        x: event.clientX,
        y: event.clientY
      };

      // 记录球的初始位置
      this.startBallPosition = { ...this.position };

      // 设置点击检测定时器
      this.clickTimer = setTimeout(() => {
        // 如果300ms内没有移动，则视为点击
        if (this.dragging) {
          this.$emit('click', event);
          this.stopDrag();
        }
      }, 300);
    },

    handleTouchStart(event) {
      this.dragging = true;
      const touch = event.touches[0];
      this.startPointerPosition = {
        x: touch.clientX,
        y: touch.clientY
      };

      // 记录球的初始位置
      this.startBallPosition = { ...this.position };

      // 设置点击检测定时器
      this.clickTimer = setTimeout(() => {
        // 如果300ms内没有移动，则视为点击
        if (this.dragging) {
          this.$emit('click', event);
          this.stopDrag();
        }
      }, 300);
    },

    onDrag(event) {
      if (!this.dragging) return;

      // 清除点击定时器（因为开始移动了）
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
      }

      // 计算偏移量
      const offsetX = event.clientX - this.startPointerPosition.x;
      const offsetY = event.clientY - this.startPointerPosition.y;

      // 基于球的初始位置计算新位置
      let newX = this.startBallPosition.x + offsetX;
      let newY = this.startBallPosition.y + offsetY;

      // 边界检查
      newX = Math.max(0, Math.min(newX, this.windowSize.width - this.size));
      newY = Math.max(0, Math.min(newY, this.windowSize.height - this.size));

      this.position = { x: newX, y: newY };
    },

    onTouchMove(event) {
      if (!this.dragging) return;

      // 清除点击定时器（因为开始移动了）
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
      }

      const touch = event.touches[0];

      // 计算偏移量
      const offsetX = touch.clientX - this.startPointerPosition.x;
      const offsetY = touch.clientY - this.startPointerPosition.y;

      // 基于球的初始位置计算新位置
      let newX = this.startBallPosition.x + offsetX;
      let newY = this.startBallPosition.y + offsetY;

      // 边界检查
      newX = Math.max(0, Math.min(newX, this.windowSize.width - this.size));
      newY = Math.max(0, Math.min(newY, this.windowSize.height - this.size));

      this.position = { x: newX, y: newY };

      // 阻止页面滚动
      event.preventDefault();
    },

    stopDrag() {
      if (!this.dragging) return;

      // 清除点击定时器
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
      }

      // 关键修复：立即更新位置参考点
      this.startBallPosition = { ...this.position };

      // // 设置延迟确保状态更新完成
      // this.dragEndTimeout = setTimeout(() => {
      //   this.dragging = false;
      //   this.snapToEdge();
      // }, 10);
      this.dragging = false;
      this.snapToEdge(); // 立即贴边
    },

    handleTouchEnd(event) {
      if (this.dragging) {
        this.stopDrag(event);
      }

      // if (this.dragEndTimeout) clearTimeout(this.dragEndTimeout);
      //
      // // 清除点击定时器
      // if (this.clickTimer) {
      //   clearTimeout(this.clickTimer);
      //   this.clickTimer = null;
      // }
      //
      // this.dragging = false;
      // this.snapToEdge();
      //
      // // 如果这里还有定时器，说明是点击
      // if (this.clickTimer) {
      //   clearTimeout(this.clickTimer);
      //   this.$emit('click', event);
      // }
    },

    snapToEdge() {
      const centerX = this.position.x + this.size / 2;
      const isLeftHalf = centerX < this.windowSize.width / 2;

      // 直接设置最终位置，不要中间状态
      const targetX = isLeftHalf
          ? this.edgeMargin
          : this.windowSize.width - this.size - this.edgeMargin;

      const targetY = Math.max(
          this.edgeMargin,
          Math.min(
              this.position.y,
              this.windowSize.height - this.size - this.edgeMargin
          )
      );

      // 直接跳转到目标位置
      this.position = { x: targetX, y: targetY };

      // 确保位置更新后重置参考点
      this.startBallPosition = { ...this.position };
    },

    handleResize() {
      this.windowSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      this.snapToEdge();
    }
  }
};
</script>

<style scoped>
.float-ball {
  /* 基础样式已移至计算属性中以便动态控制 */
}

.float-ball.dragging {
  /* 拖拽时添加缩放效果 */
  transition: transform 0.3s ease-in-out;
  transform: scale(1.05);
  z-index: 1000000; /* 确保拖拽时在最顶层 */
}
/* 添加拖拽结束时的过渡效果 */
.float-ball:not(.dragging) {
  transition:
      left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
      top 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.2s ease;
}
</style>
